import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Link } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { PageHead } from "../analytics/PageHead";
import { SubnavTabs } from "../analytics/SubnavTabs";
import { DrillPanel, StatGrid } from "../analytics/DrillPanel";
import {
  BodyCell,
  BodyRow,
  HeaderCell,
  TableShell,
} from "../analytics/TableCells";
import { KPICard } from "../Dashboard/KPICard";
import VentasB2bService, {
  type ClienteB2b,
  type ClientesB2bFiltros,
} from "../../services/ventasB2b.service";

const fmt = VentasB2bService.formatCurrency;
const fmtFecha = (iso: string | null): string => (iso ? iso.slice(0, 10) : "—");

// URL del Odoo de producción — sobreescribible por env para otros entornos.
const ODOO_BASE_URL: string =
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.["VITE_ODOO_URL"] ??
  "https://masporpieza.mx";
const abrirContactoOdoo = (partnerId: number): void => {
  window.open(`${ODOO_BASE_URL}/odoo/contacts/${partnerId}`, "_blank", "noopener");
};

const enlaceWhatsApp = (celular: string): string => {
  const digitos = celular.replace(/\D/g, "");
  const conLada = digitos.length === 10 ? `52${digitos}` : digitos;
  return `https://wa.me/${conLada}`;
};

function DatoContacto({ icono, children }: { icono: React.ReactNode; children: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ color: "var(--ink-3)", display: "flex", "& svg": { fontSize: 18 } }}>{icono}</Box>
      <Typography variant="body2" sx={{ color: "var(--ink-1)", fontWeight: 500 }}>
        {children}
      </Typography>
    </Stack>
  );
}

const COLUMNAS: { clave: string; titulo: string; align?: "right" }[] = [
  { clave: "nombre", titulo: "Cliente" },
  { clave: "total", titulo: "Ingreso", align: "right" },
  { clave: "ops", titulo: "Ops (POS/Órd)", align: "right" },
  { clave: "ticketPromedio", titulo: "Ticket", align: "right" },
  { clave: "promedioMensual", titulo: "Prom/mes", align: "right" },
  { clave: "mesesActivos", titulo: "Meses", align: "right" },
  { clave: "ultimaCompra", titulo: "Última compra", align: "right" },
];

export default function DashboardVentasB2B() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const previo = document.documentElement.dataset["accent"];
    document.documentElement.dataset["accent"] = "blue";
    return () => {
      if (previo) document.documentElement.dataset["accent"] = previo;
      else delete document.documentElement.dataset["accent"];
    };
  }, []);

  const [q, setQ] = useState("");
  const [canal, setCanal] = useState("todos");
  const [activoUltimosMeses, setActivoUltimosMeses] = useState("0");
  const [ingresoMin, setIngresoMin] = useState("");
  const [promedioMensualMin, setPromedioMensualMin] = useState("");
  const [mesesActivosMin, setMesesActivosMin] = useState("");
  const [etiquetaId, setEtiquetaId] = useState("0");
  const [defaultsListos, setDefaultsListos] = useState(false);
  const [ordenarPor, setOrdenarPor] = useState("total");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [clienteAbierto, setClienteAbierto] = useState<ClienteB2b | null>(null);
  const [vista, setVista] = useState<"clientes" | "productos">("clientes");

  const filtros: ClientesB2bFiltros = useMemo(
    () => ({
      q: q || undefined,
      canal: canal !== "todos" ? (canal as ClientesB2bFiltros["canal"]) : undefined,
      activoUltimosMeses: activoUltimosMeses !== "0" ? Number(activoUltimosMeses) : undefined,
      ingresoMin: ingresoMin ? Number(ingresoMin) : undefined,
      promedioMensualMin: promedioMensualMin ? Number(promedioMensualMin) : undefined,
      mesesActivosMin: mesesActivosMin ? Number(mesesActivosMin) : undefined,
      etiquetaId: etiquetaId !== "0" ? Number(etiquetaId) : undefined,
      ordenarPor,
      dir,
      limit: 100,
    }),
    [q, canal, activoUltimosMeses, ingresoMin, promedioMensualMin, mesesActivosMin, etiquetaId, ordenarPor, dir],
  );

  const clientesQuery = useQuery({
    queryKey: ["ventas-b2b", "clientes", filtros],
    queryFn: () => VentasB2bService.getClientes(filtros),
  });
  const kpisQuery = useQuery({
    queryKey: ["ventas-b2b", "kpis", filtros],
    queryFn: () => VentasB2bService.getKpis(filtros),
  });
  const etiquetasQuery = useQuery({
    queryKey: ["ventas-b2b", "etiquetas"],
    queryFn: () => VentasB2bService.getEtiquetas(),
    staleTime: 30 * 60 * 1000,
  });
  const detalleQuery = useQuery({
    queryKey: ["ventas-b2b", "cliente", clienteAbierto?.id],
    queryFn: () => VentasB2bService.getCliente(clienteAbierto!.id),
    enabled: clienteAbierto !== null,
  });

  const ordenar = (clave: string) => {
    if (ordenarPor === clave) setDir(dir === "desc" ? "asc" : "desc");
    else {
      setOrdenarPor(clave);
      setDir("desc");
    }
  };

  const cargandoKpis = kpisQuery.isFetching && !kpisQuery.data;
  const kpis = kpisQuery.data;

  // La línea de mayorista llega de la configuración del backend y se aplica
  // como DEFAULT editable de los filtros — el vendedor la ajusta o la borra.
  useEffect(() => {
    if (!defaultsListos && kpis) {
      setPromedioMensualMin(String(kpis.parametros.umbralPromedioMensual));
      setMesesActivosMin(String(kpis.parametros.mesesMinimos));
      setDefaultsListos(true);
    }
  }, [defaultsListos, kpis]);
  const clientes = clientesQuery.data?.clientes ?? [];
  const detalle = detalleQuery.data;

  const campoFiltro = { size: "small" as const, fullWidth: true };

  if (clientesQuery.isError) {
    return (
      <Box sx={{ p: isMobile ? 2 : 3 }}>
        <Alert severity="error">
          No se pudieron cargar los clientes B2B. Verifica tu sesión y que la réplica de Odoo esté disponible.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      <Stack spacing={3}>
        <PageHead
          title="Ventas B2B"
          subtitle="Clientes consolidados POS + órdenes de venta · ajusta los filtros para definir el corte"
          actions={
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => VentasB2bService.descargarExport(filtros)}
            >
              Exportar BDD
            </Button>
          }
        />

        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField {...campoFiltro} label="Buscar cliente" value={q} onChange={(e) => setQ(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, md: 1.5 }}>
            <TextField {...campoFiltro} type="number" label="Prom/mes mín. ($)" value={promedioMensualMin} onChange={(e) => setPromedioMensualMin(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, md: 1.5 }}>
            <TextField {...campoFiltro} type="number" label="Meses activos mín." value={mesesActivosMin} onChange={(e) => setMesesActivosMin(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, md: 1.5 }}>
            <TextField {...campoFiltro} select label="Canal" value={canal} onChange={(e) => setCanal(e.target.value)}>
              <MenuItem value="todos">Todos</MenuItem>
              <MenuItem value="pos">POS</MenuItem>
              <MenuItem value="ordenes">Órdenes</MenuItem>
              <MenuItem value="ambos">Ambos</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField {...campoFiltro} select label="Activo en últimos" value={activoUltimosMeses} onChange={(e) => setActivoUltimosMeses(e.target.value)}>
              <MenuItem value="0">Sin límite</MenuItem>
              <MenuItem value="1">1 mes</MenuItem>
              <MenuItem value="3">3 meses</MenuItem>
              <MenuItem value="6">6 meses</MenuItem>
              <MenuItem value="12">12 meses</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 1.5 }}>
            <TextField {...campoFiltro} type="number" label="Ingreso mín. ($)" value={ingresoMin} onChange={(e) => setIngresoMin(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, md: 1.5 }}>
            <TextField {...campoFiltro} select label="Vendedor" value={etiquetaId} onChange={(e) => setEtiquetaId(e.target.value)}>
              <MenuItem value="0">Todos</MenuItem>
              {(etiquetasQuery.data ?? []).map((et) => (
                <MenuItem key={et.id} value={String(et.id)}>{et.nombre}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Box sx={{ display: "grid", gap: "var(--gap-grid, 12px)", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)" }}>
          <KPICard title="Clientes" value={String(kpis?.totalClientes ?? 0)} loading={cargandoKpis} helperText="con compras registradas" />
          <KPICard title="Mayoristas" value={String(kpis?.totalMayoristas ?? 0)} loading={cargandoKpis} helperText="cumplen la línea" accent />
          <KPICard title="Ingreso total" value={fmt(kpis?.ingresoTotal ?? 0)} loading={cargandoKpis} helperText="del universo filtrado" />
          <KPICard title="Ticket promedio" value={fmt(kpis?.ticketPromedioGlobal ?? 0)} loading={cargandoKpis} helperText={`${kpis?.operaciones ?? 0} operaciones`} />
        </Box>

        <SubnavTabs<"clientes" | "productos">
          tabs={[
            { key: "clientes", label: "Clientes", badge: clientesQuery.data?.total },
            { key: "productos", label: "Productos" },
          ]}
          active={vista}
          onChange={setVista}
        />

        {vista === "clientes" && (
        <Box sx={{ backgroundColor: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)" }}>
          <TableShell>
            <Box component="thead">
              <Box component="tr">
                <HeaderCell width={40}>{""}</HeaderCell>
                {COLUMNAS.map((col) => (
                  <HeaderCell key={col.clave} align={col.align ?? "left"}>
                    <Box
                      component="span"
                      onClick={() => ordenar(col.clave)}
                      sx={{ cursor: "pointer", userSelect: "none", color: ordenarPor === col.clave ? "var(--accent)" : undefined }}
                    >
                      {col.titulo}
                      {ordenarPor === col.clave ? (dir === "desc" ? " ↓" : " ↑") : ""}
                    </Box>
                  </HeaderCell>
                ))}
                <HeaderCell>Etiquetas</HeaderCell>
              </Box>
            </Box>
            <Box component="tbody">
              {clientesQuery.isLoading ? (
                <Box component="tr">
                  <BodyCell align="left">
                    <Skeleton height={160} />
                  </BodyCell>
                </Box>
              ) : (
                clientes.map((cliente) => (
                  <BodyRow key={cliente.id} onClick={() => setClienteAbierto(cliente)}>
                    <BodyCell width={40}>
                      <Tooltip title="Abrir contacto en Odoo">
                        <IconButton
                          size="small"
                          onClick={(evento) => {
                            evento.stopPropagation();
                            abrirContactoOdoo(cliente.id);
                          }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </BodyCell>
                    <BodyCell>
                      <Box component="span" sx={{ fontWeight: 500 }}>{cliente.nombre}</Box>
                    </BodyCell>
                    <BodyCell align="right">{fmt(cliente.total)}</BodyCell>
                    <BodyCell align="right" muted>
                      {cliente.ops} ({cliente.opsPos}/{cliente.opsOrdenes})
                    </BodyCell>
                    <BodyCell align="right">{fmt(cliente.ticketPromedio)}</BodyCell>
                    <BodyCell align="right">{fmt(cliente.promedioMensual)}</BodyCell>
                    <BodyCell align="right" muted>{cliente.mesesActivos}</BodyCell>
                    <BodyCell align="right" muted>{fmtFecha(cliente.ultimaCompra)}</BodyCell>
                    <BodyCell muted>{cliente.etiquetas ?? "—"}</BodyCell>
                  </BodyRow>
                ))
              )}
            </Box>
          </TableShell>
          <Box sx={{ px: 1.5, py: 1, borderTop: "1px solid var(--border)" }}>
            <Typography variant="caption" sx={{ color: "var(--ink-3)" }}>
              {clientesQuery.data
                ? `${clientes.length} de ${clientesQuery.data.total} clientes · clic en una fila para el detalle · máx. 100 visibles, acota con filtros`
                : ""}
            </Typography>
          </Box>
        </Box>
        )}

        {vista === "productos" && (
        <Box sx={{ backgroundColor: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "var(--ink-2)" }}>
            Top productos del universo filtrado
          </Typography>
          {cargandoKpis ? (
            <Skeleton height={120} />
          ) : (
            <TableShell>
              <Box component="tbody">
                {(kpis?.topProductosGlobal ?? []).map((prod, indice) => (
                  <BodyRow key={prod.producto}>
                    <BodyCell muted width={28}>{indice + 1}</BodyCell>
                    <BodyCell>{prod.producto}</BodyCell>
                    <BodyCell align="right">{fmt(prod.venta)}</BodyCell>
                    <BodyCell align="right" muted>{Math.round(prod.piezas)} pzas</BodyCell>
                  </BodyRow>
                ))}
              </Box>
            </TableShell>
          )}
        </Box>
        )}
      </Stack>

      <DrillPanel
        open={clienteAbierto !== null}
        title={clienteAbierto?.nombre ?? ""}
        subtitle={detalle?.cliente.etiquetas ?? undefined}
        onClose={() => setClienteAbierto(null)}
      >
        {detalleQuery.isLoading ? (
          <Skeleton height={280} />
        ) : detalle ? (
          <Stack spacing={2}>
            <Box
              sx={{
                backgroundColor: "var(--bg-2, rgba(0,0,0,0.03))",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm, 6px)",
                p: 1.5,
              }}
            >
              <Stack spacing={1}>
                <DatoContacto icono={<PlaceOutlinedIcon />}>
                  {detalle.cliente.direccion || "Sin dirección"}
                </DatoContacto>
                <DatoContacto icono={<PhoneOutlinedIcon />}>
                  {detalle.cliente.telefono ? (
                    <Link href={`tel:${detalle.cliente.telefono}`} underline="hover">
                      {detalle.cliente.telefono}
                    </Link>
                  ) : (
                    "—"
                  )}
                </DatoContacto>
                <DatoContacto icono={<WhatsAppIcon />}>
                  {detalle.cliente.celular ? (
                    <Link
                      href={enlaceWhatsApp(detalle.cliente.celular)}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                    >
                      {detalle.cliente.celular}
                    </Link>
                  ) : (
                    "—"
                  )}
                </DatoContacto>
                <DatoContacto icono={<EmailOutlinedIcon />}>
                  {detalle.cliente.correo ? (
                    <Link href={`mailto:${detalle.cliente.correo}`} underline="hover">
                      {detalle.cliente.correo}
                    </Link>
                  ) : (
                    "—"
                  )}
                </DatoContacto>
              </Stack>
            </Box>
            <StatGrid
              items={[
                { label: "Ingreso total", value: fmt(detalle.cliente.total) },
                { label: "Prom. mensual", value: fmt(detalle.cliente.promedioMensual), helper: `${detalle.cliente.mesesActivos} meses activos` },
                { label: "Ticket promedio", value: fmt(detalle.cliente.ticketPromedio), helper: `${detalle.cliente.ops} operaciones` },
                { label: "Última compra", value: fmtFecha(detalle.cliente.ultimaCompra), helper: `cliente desde ${fmtFecha(detalle.cliente.fechaCreacion)}` },
              ]}
            />
            <Typography variant="subtitle2" sx={{ color: "var(--ink-2)" }}>Compras por mes y canal</Typography>
            <TableShell maxHeight={220}>
              <Box component="tbody">
                {[...detalle.serieMensual].reverse().map((punto) => (
                  <BodyRow key={`${punto.mes}-${punto.canal}`}>
                    <BodyCell>{punto.mes}</BodyCell>
                    <BodyCell muted>{punto.canal === "pos" ? "POS" : "Órdenes"}</BodyCell>
                    <BodyCell align="right">{fmt(punto.monto)}</BodyCell>
                    <BodyCell align="right" muted>{punto.ops} ops</BodyCell>
                  </BodyRow>
                ))}
              </Box>
            </TableShell>
            <Typography variant="subtitle2" sx={{ color: "var(--ink-2)" }}>Top productos del cliente</Typography>
            <TableShell maxHeight={240}>
              <Box component="tbody">
                {detalle.topProductos.map((prod) => (
                  <BodyRow key={prod.producto}>
                    <BodyCell>{prod.producto}</BodyCell>
                    <BodyCell align="right">{fmt(prod.venta)}</BodyCell>
                  </BodyRow>
                ))}
              </Box>
            </TableShell>
          </Stack>
        ) : (
          <Alert severity="error">No se pudo cargar el detalle del cliente.</Alert>
        )}
      </DrillPanel>
    </Box>
  );
}
